// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import config from "@config";
import type { PaginationType, PropertiesSchema } from "@types";
import actionCreators from "@modules/service/actionCreator";
import type {
  ReportingParametersData,
  ReasonToBeExcluded,
} from "@modules/service/reducer/reporting-parameters/types";
import {
  GetReportingParameters,
  UpdateReportingParameters,
  GetReportingParametersSchema,
  GetReasonsToBeExcluded,
  GetReasonsToBeExcludedSchema,
  CreateReasonToBeExcluded,
  UpdateReasonToBeExcluded,
  DeleteReasonToBeExcluded,
} from "@modules/service/constants/actions";
import { selectReasonsToBeExcludedPage } from "@modules/service/selectors/reporting-parameters";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { getAbsenceReasonsList } from "@modules/service/selectors/absence-reasons";
import { onGetAbsenceReasonsList } from "./absence-reasons";
import { getSchema, parseSchema } from "@utils/schema";

export type ListResponse<T> = $Exact<{
  content: T,
  paginations: PaginationType,
}>;

type ReportingParametersRequest = $Exact<{
  clientId: string,
  absenceReasonTypeId: number,
  isActive: boolean,
  page: number | null,
}>;

export function* onGetReportingParameters({
  payload,
}: Action<ReportingParametersRequest>): Generator<*, void, { data: ReportingParametersData }> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/reporting-parameters`,
    );

    yield put(actionCreators.GetReportingParametersSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetReportingParametersError(e.response));
  }
}

type UpdateReportingParametersRequest = $Exact<{
  ...ReportingParametersRequest,
  formData: Object,
}>;

export function* onUpdateReportingParameters({
  payload,
}: Action<UpdateReportingParametersRequest>): Generator<
  *,
  void,
  { data: ReportingParametersData },
> {
  try {
    if (!payload || !payload.clientId || !payload.formData) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/reporting-parameters`,
      payload.formData,
    );

    yield all([
      put(actionCreators.UpdateReportingParametersSuccess(response.data)),
      put(sendSuccessMessage("REPORTING_PARAMETERS_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateReportingParametersError(e.response));
  }
}

export function* onGetReportingParametersSchema(): Generator<*, void, PropertiesSchema> {
  try {
    const data = getSchema("ReportingParametersServiceSectionDTO");

    yield put(actionCreators.GetReportingParametersSchemaSuccess(data));
  } catch (e) {
    yield put(actionCreators.GetReportingParametersSchemaError(e.response));
  }
}

type ReasonsToBeExcludedRequest = $Exact<{
  ...ReportingParametersRequest,
  page: number,
}>;

export function* onGetReasonsToBeExcluded({
  payload,
}: Action<ReasonsToBeExcludedRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<ReasonToBeExcluded>> },
> {
  const currentPage = yield select(selectReasonsToBeExcludedPage);
  const page = (payload && payload.page) || currentPage;

  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/absence-rate-calc-exclusions`,
      {
        params: {
          page,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetReasonsToBeExcludedSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetReasonsToBeExcludedError(e.response));
  }
}

export function* onGetReasonsToBeExcludedSchema({
  payload,
}: Action<ReportingParametersRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield onGetAbsenceReasonsList({ payload, type: "" });
    const absenceReasons = yield select(getAbsenceReasonsList);

    const data = yield parseSchema("ClientAbsenceReasonRateCalcExclusionDTO", {
      clientId: payload.clientId,
    });

    const absenceReasonFieldTitle = (((data || {}).properties || {}).absenceReason || {}).title;
    const schemaWithAbsenceReasons = {
      ...data,
      properties: {
        ...data.properties,
        absenceReason: {
          ...data.properties.absenceReason,
          properties: {
            ...data.properties.absenceReason.properties,
            id: {
              type: "integer",
              title: absenceReasonFieldTitle,
              oneOf: (absenceReasons || []).map(reason => ({
                title: reason.content.displayName,
                const: reason.id,
              })),
            },
          },
        },
      },
    };

    yield put(actionCreators.GetReasonsToBeExcludedSchemaSuccess(schemaWithAbsenceReasons));
  } catch (e) {
    yield put(actionCreators.GetReasonsToBeExcludedSchemaError(e));
  }
}

type CreateReasonToBeExcludedRequest = $Exact<{
  ...ReportingParametersRequest,
  formData: $Exact<{
    absenceReason: $Exact<{
      id: number,
    }>,
    excludeFrom: string,
    excludeTo: string,
  }>,
}>;

export function* onCreateReasonToBeExcluded({
  payload,
}: Action<CreateReasonToBeExcludedRequest>): Generator<*, void, { data: ReasonToBeExcluded }> {
  try {
    if (!payload || !payload.clientId || !payload.formData) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/absence-rate-calc-exclusion`,
      payload.formData,
    );

    yield all([
      put(actionCreators.CreateReasonToBeExcludedSuccess(response.data)),
      put(sendSuccessMessage("EXCLUDED_REASON_CREATED")),
      put(actionCreators.GetReasonsToBeExcluded({ clientId: payload.clientId })),
    ]);
  } catch (e) {
    yield put(actionCreators.CreateReasonToBeExcludedError(e.response));
  }
}

type UpdateReasonToBeExcludedRequest = $Exact<{
  ...ReportingParametersRequest,
  ...CreateReasonToBeExcludedRequest,
  reasonToBeExcludedId: number,
}>;

export function* onUpdateReasonToBeExcluded({
  payload,
}: Action<UpdateReasonToBeExcludedRequest>): Generator<*, void, { data: ReasonToBeExcluded }> {
  try {
    if (!payload || !payload.clientId || !payload.formData || !payload.reasonToBeExcludedId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/client-absence-reason-rate-calc-exclusion/${payload.reasonToBeExcludedId}`,
      payload.formData,
    );

    yield all([
      put(actionCreators.UpdateReasonToBeExcludedSuccess(response.data)),
      put(sendSuccessMessage("EXCLUDED_REASON_UPDATED")),
      put(actionCreators.GetReasonsToBeExcluded({ clientId: payload.clientId })),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateReasonToBeExcludedError(e.response));
  }
}

type DeleteReasonToBeExcludedRequest = $Exact<{
  ...ReportingParametersRequest,
  reasonToBeExcludedIdList: Array<number>,
}>;

export function* onDeleteReasonToBeExcluded({
  payload,
}: Action<DeleteReasonToBeExcludedRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.reasonToBeExcludedIdList) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield all(
      payload.reasonToBeExcludedIdList.map(id =>
        call(
          http.delete,
          `/api/v1/service-admin/client/${payload.clientId}/client-absence-reason-rate-calc-exclusion/${id}/absence-rate-calc-exclusion`,
        ),
      ),
    );

    const successMessage =
      payload.reasonToBeExcludedIdList.length > 1
        ? "SELECTED_EXCLUDED_REASONS_DELETED"
        : "EXCLUDED_REASON_DELETED";

    yield all([
      put(actionCreators.DeleteReasonToBeExcludedSuccess()),
      put(sendSuccessMessage(successMessage)),
      put(actionCreators.GetReasonsToBeExcluded({ clientId: payload.clientId })),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteReasonToBeExcludedError(e.response));
  }
}

export function* ReportingParametersSaga(): Generator<*, void, void> {
  yield all([
    takeEvery(GetReportingParameters, onGetReportingParameters),
    takeEvery(UpdateReportingParameters, onUpdateReportingParameters),
    takeEvery(GetReportingParametersSchema, onGetReportingParametersSchema),
    takeEvery(GetReasonsToBeExcluded, onGetReasonsToBeExcluded),
    takeEvery(GetReasonsToBeExcludedSchema, onGetReasonsToBeExcludedSchema),
    takeEvery(CreateReasonToBeExcluded, onCreateReasonToBeExcluded),
    takeEvery(UpdateReasonToBeExcluded, onUpdateReasonToBeExcluded),
    takeEvery(DeleteReasonToBeExcluded, onDeleteReasonToBeExcluded),
  ]);
}

export default ReportingParametersSaga;
